/**
 * UserTagsView.jsx - Vista detallada de tags de un usuario específico
 * 
 * RESPONSABILIDAD:
 * - Mostrar TODOS los tags/objetos que pertenecen a UN usuario específico
 * - Permitir gestión individual de cada tag (cambiar plan, extender vigencia)
 * - Mostrar información del propietario en la parte superior
 * - Proveer navegación de regreso a la lista de usuarios
 * 
 * PROPÓSITO EN EL SISTEMA:
 * - Vista de detalle cuando se selecciona "Ver Tags" de un usuario
 * - Permite administrar todos los objetos de un cliente específico
 * - Facilita operaciones masivas sobre los tags de un usuario
 * 
 * DIFERENCIA CON UsuariosView:
 * - UsuariosView = Lista de USUARIOS
 * - UserTagsView = Lista de TAGS de UN usuario
 * 
 * PROPS:
 * @param {String} userId - ID del usuario cuyos tags se mostrarán
 * @param {Function} onBack - Callback para regresar a usuarios
 * @param {String} baseURL - URL base para generar enlaces
 * @param {Function} loadObjetosUsuario - Función para cargar objetos del usuario
 */
/**
 * UserTagsView.jsx - Vista detallada de tags de un usuario específico
 */
import React, { useState, useEffect } from 'react';
import RecordCard from './RecordCard';

const UserTagsView = ({ userId, onBack, baseURL, addToHistory, loadObjetosUsuario }) => {
  const [objetos, setObjetos] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [userData, setUserData] = useState(null);
  const [linksModal, setLinksModal] = useState(null);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const response = await loadObjetosUsuario(userId);

      // ✅ CORRECCIÓN: Verificar estructura de respuesta
      if (response.error) {
        console.error('Error cargando tags:', response.error);
        setObjetos([]);
        return;
      }

      // ✅ Extraer el array del objeto de respuesta
      if (response.success && response.data) {
        setObjetos(response.data);
      } else if (Array.isArray(response)) {
        // Por si acaso devuelve un array directamente
        setObjetos(response);
      } else {
        console.warn('Formato de respuesta inesperado:', response);
        setObjetos([]);
      }
      
    } catch (error) {
      console.error('❌ Error cargando tags del usuario:', error);
      setObjetos([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (tagId, newPlan) => {
    // Implementar cambio de plan
    addToHistory(`Plan cambiado a ${newPlan} para tag ${tagId}`);
    await loadUserData();
  };

  const handleGenerateLinks = (record) => {
    const searchUrl = `${baseURL}/consultar?registro=${record.id}`;
    const activateUrl = `${baseURL}/activar?activacion=${record.codigoAcceso}`;
    
    setLinksModal({ 
      searchUrl, 
      activateUrl,
      recordName: record.nombre || 'Registro',
      recordId: record.id,
      folio: record.folio,
      tipo: record.tipo
    });
    
    addToHistory(`Enlaces generados para: ${record.nombre || record.id}`);
  };

  return (
    <div className="user-tags-view">
      <div className="mb-4">
        <button 
          className="btn btn-outline-secondary mb-3"
          onClick={onBack}
        >
          <i className="bi bi-arrow-left me-2"></i>
          Volver a usuarios
        </button>

        {/* Mostrar info básica mientras se carga userData completo */}
        <div className="card mb-3">
          <div className="card-body">
            <h5>
              <i className="bi bi-person-circle me-2"></i>
              Usuario: {userId}
            </h5>
            <div className="mt-2">
              <span className="badge bg-primary me-2">
                {objetos.length} tags totales
              </span>
              <span className="badge bg-success">
                {objetos.filter(o => o.estado === 'activo').length} activos
              </span>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando tags...</span>
          </div>
          <p className="mt-2 text-muted">Cargando tags del usuario...</p>
        </div>
      ) : objetos.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-tags" style={{fontSize: '3rem'}}></i>
          <p className="mt-3">Este usuario no tiene tags registrados</p>
        </div>
      ) : (
        <div className="records-list">
          {objetos.map(objeto => (
            <RecordCard
              key={objeto.id}
              record={objeto}
              selected={false}
              onToggleSelect={() => {}}
              onPlanChange={handlePlanChange}
              onExtendVigencia={() => {}} // ← Agregar handler vacío
              onPipelineAdvance={() => {}} // ← Agregar handler vacío
              onToggleLost={() => {}} // ← Agregar handler vacío
              onGenerateLinks={handleGenerateLinks}
              baseURL={baseURL}
              compact={false}
              loading={false}
            />
          ))}
        </div>
      )}

      {/* Modal de enlaces */}
      {linksModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-link-45deg me-2"></i>
                  Enlaces de {linksModal.recordName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setLinksModal(null)}
                />
              </div>
              <div className="modal-body">
                <div className="alert alert-info small mb-3">
                  <strong>Registro:</strong> {linksModal.recordName}<br/>
                  <strong>ID:</strong> {linksModal.recordId}<br/>
                  <strong>Folio:</strong> {linksModal.folio}<br/>
                  {linksModal.tipo && <><strong>Tipo:</strong> {linksModal.tipo}</>}
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    <i className="bi bi-search me-2"></i>
                    Enlace de Búsqueda (Consulta Pública):
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={linksModal.searchUrl}
                      readOnly
                    />
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(linksModal.searchUrl);
                        const btn = document.activeElement;
                        const originalText = btn.innerHTML;
                        btn.innerHTML = '<i class="bi bi-check"></i> Copiado';
                        setTimeout(() => {
                          if (btn) btn.innerHTML = originalText;
                        }, 2000);
                      }}
                    >
                      <i className="bi bi-clipboard"></i> Copiar
                    </button>
                  </div>
                </div>

                <div>
                  <label className="form-label fw-bold">
                    <i className="bi bi-key me-2"></i>
                    Enlace de Activación:
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={linksModal.activateUrl}
                      readOnly
                    />
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(linksModal.activateUrl);
                        const btn = document.activeElement;
                        const originalText = btn.innerHTML;
                        btn.innerHTML = '<i class="bi bi-check"></i> Copiado';
                        setTimeout(() => {
                          if (btn) btn.innerHTML = originalText;
                        }, 2000);
                      }}
                    >
                      <i className="bi bi-clipboard"></i> Copiar
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setLinksModal(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTagsView;